// src/components/AppBar.tsx
import * as React from 'react';
import Box from '@mui/material/Box';
import { useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import { useUser } from '../../context/UserContext'; // useUser をインポート
import { useNavigate } from 'react-router-dom';
import { axiosRequest } from '../../service/AxiosService';
import LogoutIcon from '@mui/icons-material/Logout';
interface AppBarProps {
  open: boolean;
  handleDrawerOpen: () => void;
}

/**
 * ログアウトボタンコンポーネント
 * - クリックするとサーバーへのログアウトリクエストを実行し、ログイン画面に遷移する
 */
export const LogoutButton = () => {
  const navigate = useNavigate();

  // 保守モードのみログアウトボタンを表示する（通常モードでは非表示）
  const [visible, setVisible] = React.useState(false);
  React.useEffect(() => {
    const fetchConfig = async () => {
      try {
        const config = await axiosRequest<{ maintenanceMode: boolean }>(
          "/config",
          "GET"
        );
        setVisible(config?.maintenanceMode === true);
      } catch (error) {
        console.error("設定取得エラー", error);
      }
    };
    fetchConfig();
  }, []);

  // ログアウト処理：サーバーへPOSTリクエスト（Cookie削除・refresh token無効化）後、
  // バックエンド経由でCognitoセッションのログアウトへ遷移する。
  // CognitoのログアウトURLの組立はバックエンド（/api/auth/logout-redirect）が行う。
  const handleLogout = async () => {
    try {
      await axiosRequest<void>("/logout", "POST");
      window.location.href = `${process.env.REACT_APP_BASE_URL}auth/logout-redirect`;
    } catch (error) {
      console.error("ログアウトエラー", error);
      navigate("/login");
    }
  };

  if (!visible) return null;
  return <IconButton color="primary" onClick={handleLogout} sx={{ ml: 1, backgroundColor: 'transparent', '&:hover': { backgroundColor: 'white', } }}><LogoutIcon fontSize="small" /></IconButton>;
};

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open?: boolean }>(({ theme, open }) => ({
  background: theme.palette.custom.bluePinkGradient2,
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: 220,
    width: `calc(100% - 370px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

/**
 * アプリケーションバーコンポーネント
 * - システム名・ロゴ・ログインユーザー名・ログアウトボタンを表示する
 * - props: open (ドロワー展開状態), handleDrawerOpen (ドロワー開閉ハンドラー)
 */
const AppBarComponent: React.FC<AppBarProps> = ({ open, handleDrawerOpen }) => {
  const isMobile = useMediaQuery('(max-width:600px)');  // 600px 以下でモバイル表示
  const { user, loading } = useUser();  // useUser フックでユーザー情報を取得

  return (
    <AppBar open={open} sx={{
      height: isMobile ? '48px' : '56px', display: 'flex',
      justifyContent: 'center'
    }}>
      <Toolbar sx={{
        height: '100%'
      }}>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          onClick={handleDrawerOpen}
          edge="start"
          sx={{

            mr: 1,
            ...(open && { display: 'none' }),
          }}
        >
          <MenuIcon />
        </IconButton>
        <Box sx={{padding: '4px 4px', mt:1}}>
        <img src="/logo1.png" alt="企業ロゴ" style={{ height: isMobile ? '28px' : '42px', backgroundColor: 'white',padding: '4px 4px',  borderRadius: '4px' }} />
        </Box>
        <Typography variant={isMobile ? 'h3' : 'h1'} sx={{ ml: 2,  color: '#FFFFFF' }}>
          統合WF
        </Typography>

        {/* ユーザー情報がある場合のみ表示 */}
        {!isMobile && !loading && user && (

          <Typography variant="h3" sx={{ ml: 'auto', color: '#666666', fontWeight: 'bold' }}>
            ようこそ、{user.lastName + user.firstName}さん！
          </Typography>

        )}

        {/* ユーザー情報があり、モバイルの場合のみ表示 */}
        {isMobile && !loading && user && (
          <Typography variant="h3" sx={{ ml: 'auto', color: '#666666', fontWeight: 'bold' }}>
            {user.lastName + user.firstName}
          </Typography>
        )}
        <LogoutButton />

        {/* ローディング中 */}
        {loading && !isMobile && (
          <Typography variant="h3" sx={{ ml: 'auto', color: '#666666', fontWeight: 'bold' }}>
            ローディング...
          </Typography>
        )}
      </Toolbar>
    </AppBar>
  );
}

export default AppBarComponent;
